import moment from 'moment';
import { useCallback, useMemo, useState, type ChangeEventHandler } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  useGetProductOperationsListsQuery,
  useLazyGetProductOperationsSearchResultQuery,
  useSearchProductOperationsMutation,
} from '@app/api/Products';
import type {
  ModelProductOperation,
  ModelProductOperationsSearchFilter,
} from '@app/api/Products';
import { GOODS_UNITS_PATH } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { getOptions } from '@app/pages/Warehouses/utils';
import {
  initialFilters,
  initPagination,
  getWarehouseOperationsColumns,
} from '@app/pages/Warehouses/WarehouseOperations/data';
import type {
  FilterChangeType,
  OperationsFilters,
  Pagination,
} from '@app/pages/Warehouses/WarehouseOperations/types';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { useErrorHandler } from '@app/shared/hooks';
import type { Options } from '@app/types';
import { clog } from '@app/utils/clog';

import type { PaginationProps } from 'antd';

export const useWarehouseOperations = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const {
    params: { ActiveBranchId },
  } = useUserPrint();

  const [warehousesOptions, setWarehousesOptions] = useState<Options>([]);
  const [operationTypesOptions, setOperationTypesOptions] = useState<Options>(
    []
  );
  const [productsOptions, setProductsOptions] = useState<Options>([]);
  const [unsavedFilters, setUnsavedFilters] = useState<OperationsFilters>(
    () => initialFilters
  );

  const [filters, setFilters] = useState<OperationsFilters>(
    () => initialFilters
  );
  const [searchId, setSearchId] = useState<number | null>(null);

  const [pagination, setPagination] = useState<Pagination>(
    () => initPagination
  );

  const [tableData, setTableData] = useState<ModelProductOperation[]>([]);

  const [searchProductOperations, { isLoading, isUninitialized }] =
    useSearchProductOperationsMutation();
  const [
    getProductOperationsSearchResult,
    { isLoading: isGetProductOperationsSearchResultLoading },
  ] = useLazyGetProductOperationsSearchResultQuery();

  const {
    data: filtersLists,
    isFetching: isFiltersListsFetching,
    isLoading: isFiltersListsLoading,
    isUninitialized: isFiltersListsUninitialized,
    isError: isFiltersListsError,
    error: filtersListsError,
  } = useGetProductOperationsListsQuery(
    { ActiveBranchId },
    {
      skip: !ActiveBranchId,
      refetchOnMountOrArgChange: true,
      refetchOnReconnect: true,
    }
  );

  useErrorHandler([{ flag: isFiltersListsError, error: filtersListsError }]);

  const isPageLoading = useMemo(
    () =>
      isLoading || isUninitialized || isGetProductOperationsSearchResultLoading,
    [isLoading, isUninitialized, isGetProductOperationsSearchResultLoading]
  );
  const isFetching = useMemo(() => isLoading, [isLoading]);

  const disabledFilters = useMemo(
    () =>
      isFiltersListsLoading ||
      isFiltersListsFetching ||
      isFiltersListsUninitialized,
    [isFiltersListsLoading, isFiltersListsFetching, isFiltersListsUninitialized]
  );

  const openDocument = useCallback((id: number) => {
    clog(`Open document ${id}`);
  }, []);

  const openProduct = useCallback(
    (id: number) => {
      navigate(`${GOODS_UNITS_PATH}?Products=${id}`);
    },
    [navigate]
  );

  const openRecord = useCallback((id: number) => {
    clog(`Open record ${id}`);
  }, []);

  const openUser = useCallback((id: number) => {
    clog(`Open user ${id}`);
  }, []);

  const columns = useMemo(
    () =>
      getWarehouseOperationsColumns({
        openDocument,
        openProduct,
        openRecord,
        openUser,
      }),
    [openDocument, openProduct, openRecord, openUser]
  );

  const onFilterChange: FilterChangeType = useCallback((key, value) => {
    setUnsavedFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const onChangeSearchTerm: ChangeEventHandler<HTMLInputElement> = useCallback(
    ({ target: { value: SearchTerm } }) => {
      onFilterChange('SearchTerm', SearchTerm);
    },
    [onFilterChange]
  );

  const skipFilters = useCallback(() => {
    setUnsavedFilters(initialFilters);
    setFilters(initialFilters);
    setPagination(initPagination);
  }, []);

  const onSearch = useCallback(() => {
    setPagination(initPagination);
    setFilters(unsavedFilters);
  }, [unsavedFilters]);

  const handlePaginationChange = useCallback(
    async (page: number, pageSize: number) => {
      if (!searchId) return;

      const { Operations } = await getProductOperationsSearchResult({
        ActiveBranchId,
        pageN: page,
        itemsPerPage: pageSize,
        searchId: searchId,
      }).unwrap();

      setTableData(Operations ?? []);
    },
    [ActiveBranchId, getProductOperationsSearchResult, searchId]
  );

  const onPaginate: PaginationProps['onChange'] = useCallback(
    (page: number, pageSize: number) => {
      setPagination((prev) => ({
        ...prev,
        Page: page,
        ItemsPerPage: pageSize,
      }));

      handlePaginationChange(page, pageSize);
    },
    [handlePaginationChange]
  );

  const filtersListsHandler = useCallback(() => {
    if (!filtersLists) return;

    const { Products, OperationTypes, Warehouses } = filtersLists;

    const warehousesOptions = getOptions(Warehouses);
    const operationTypesOptions = getOptions(OperationTypes, 'IdS');
    const productsOptions = getOptions(Products);

    setWarehousesOptions(warehousesOptions);
    setOperationTypesOptions(operationTypesOptions);
    setProductsOptions(productsOptions);
  }, [filtersLists]);

  const searchProductOperationsHandler = useCallback(async () => {
    const {
      range,
      SearchTerm,
      SearchBy,
      warehouses,
      operationTypes,
      products,
    } = filters;

    const DateS = range?.[0]?.format('YYYY-MM-DD');
    const DateE = range?.[1]?.format('YYYY-MM-DD');

    const searchFilter: ModelProductOperationsSearchFilter = {
      DateS: DateS
        ? moment(DateS).startOf('day').format('YYYY-MM-DDTHH:mm:ss')
        : null,
      DateE: DateE
        ? moment(DateE).endOf('day').format('YYYY-MM-DDTHH:mm:ss')
        : null,
      SearchTerm,
      SearchBy,
      WarehouseIds: warehouses?.map(({ value }) => Number(value)) ?? null,
      ProductIds: products?.map(({ value }) => Number(value)) ?? null,
      OperationTypeIds:
        operationTypes
          ?.map(({ value }) => value?.toString() || '')
          .filter(Boolean) ?? null,
      ItemsPerPage: initPagination.ItemsPerPage,
      PageN: initPagination.Page,
    };

    try {
      const response = await searchProductOperations({
        data: searchFilter,
        ActiveBranchId,
      }).unwrap();

      const { Operations, PageN, CountOperations, Id } = response;
      setSearchId(Id);
      setPagination((prev) => ({
        ...prev,
        Page: PageN !== prev.Page ? PageN : prev.Page,
        Total: CountOperations,
      }));
      setTableData(Operations ?? []);
    } catch (error) {
      showNotification(error);
    }
  }, [filters, showNotification, searchProductOperations, ActiveBranchId]);

  return {
    isFetching,
    isPageLoading,
    disabledFilters,
    warehousesOptions,
    operationTypesOptions,
    productsOptions,
    filtersListsHandler,
    unsavedFilters,
    onFilterChange,
    onChangeSearchTerm,
    skipFilters,
    onSearch,
    onPaginate,
    pagination,
    searchProductOperationsHandler,
    tableData,
    columns,
  };
};
